import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { StudyShare, StudyShareService } from '../../../services/study-share.service';
import { DicomViewerDialogComponent } from '../dicom-viewer-dialog/dicom-viewer-dialog.component';

interface FacilityGroup {
  facilityId: string;
  shares: StudyShare[];
  expanded: boolean;
}

@Component({
  selector: 'app-study-share-dashboard',
  templateUrl: './study-share-dashboard.component.html',
  styleUrls: ['./study-share-dashboard.component.scss'],
  standalone: false,
})
export class StudyShareDashboardComponent implements OnInit, OnDestroy {
  @Input() hubFacilityId!: string;

  facilityGroups: FacilityGroup[] = [];
  totalShares = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private studyShareService: StudyShareService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.studyShareService.watchSharesSentByFacility(this.hubFacilityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: shares => {
          this.totalShares = shares.length;
          this.facilityGroups = this.groupByFacility(shares);
        },
        error: err => console.error('[StudyShareDashboard] watchSharesSentByFacility failed:', err),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private groupByFacility(shares: StudyShare[]): FacilityGroup[] {
    const map = new Map<string, StudyShare[]>();
    for (const share of shares) {
      const key = share.shared_to_facility_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(share);
    }
    // Preserve existing expanded state on re-renders
    const existingExpanded = new Map(this.facilityGroups.map(g => [g.facilityId, g.expanded]));
    return [...map.entries()].map(([facilityId, facilityShares]) => ({
      facilityId,
      shares: facilityShares,
      expanded: existingExpanded.get(facilityId) ?? true,
    }));
  }

  toggleGroup(group: FacilityGroup): void {
    group.expanded = !group.expanded;
  }

  formatSharedAt(share: StudyShare): string {
    if (!share.shared_at) return '';
    return share.shared_at.toDate().toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }

  activeCount(group: FacilityGroup): number {
    return group.shares.filter(s => s.status === 'active' || s.status === 'expiring_soon').length;
  }

  pendingCount(group: FacilityGroup): number {
    return group.shares.filter(s => s.status === 're_requested').length;
  }

  onViewStudy(share: StudyShare): void {
    this.dialog.open(DicomViewerDialogComponent, {
      width: '860px',
      data: { study: { studyInstanceUid: share.study_instance_uid } },
    });
  }
}
